import Vue from 'vue'
import Router from 'vue-router'
import store from './store'
import Home from './views/Home.vue'
import Login from './views/Login.vue'
import ForgotPassword from './views/ForgotPassword'
import ResetPassword from './views/ResetPassword'
import MenuPage from '@/views/MenuPage'
import Notifications from '@/views/Notifications.vue'
import Profile from './views/Profile.vue'
import PropertyFeeds from '@/views/PropertyFeeds.vue'
import PropertyManagement from '@/views/PropertyManagement.vue'
import PropertyContentManagement from '@/views/PropertyContentManagement.vue'
import News from '@/views/News.vue'
import Page from '@/views/Page'
import Error404 from '@/views/errors/404'
import AvailabilityCalendar from '@/views/AvailabilityCalendar'
import Social from '@/views/Social'
import EasyPropertySSO from '@/views/EasyPropertySSO'
import Partners from '@/views/Partners'
import ProfileV2 from '@/views/ProfileV2.vue'
import FcAnnualReview from '@/views/annual-review/FcAnnualReview.vue'
import GuildAnnualReview from '@/views/annual-review/GuildAnnualReview.vue'
import QrGenerator from '@/views/qr-generator/QrGenerator.vue'

Vue.use(Router)

let router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior (to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }

    if (to.hash) {
      return {
        selector: to.hash
      }
    }

    return { x: 0, y: 0 }
  },
  routes: [
    {
      path: '/',
      name: 'home',
      component: Home,
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/components',
      name: 'components',
      component: () => import('@/views/Components'),
      meta: {
        requiresAuthentication: false
      }
    },
    {
      path: '/widgets',
      name: 'widgets',
      component: () => import('@/views/Widgets'),
      meta: {
        requiresAuthentication: false
      }
    },
    {
      path: '/oauth/login',
      name: 'login',
      component: Login,
      meta: {
        requiresAuthentication: false
      }
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: ForgotPassword,
      meta: {
        requiresAuthentication: false
      }
    },
    {
      path: '/forgot-password/reset/:token',
      name: 'reset-password',
      component: ResetPassword,
      meta: {
        requiresAuthentication: false
      }
    },
    {
      path: '/admin',
      name: 'admin',
      component: () => import('@/views/cms/Admin'),
      meta: {
        requiresAuthentication: true,
        adminOnly: true
      }
    },
    {
      path: '/admin/page',
      name: 'new-page',
      component: () => import('@/views/cms/Page'),
      meta: {
        requiresAuthentication: true,
        adminOnly: true,
        title: 'New Page'
      }
    },
    {
      path: '/admin/page/:id',
      name: 'edit-page',
      props: true,
      component: () => import('@/views/cms/Page'),
      meta: {
        requiresAuthentication: true,
        adminOnly: true
      }
    },
    {
      path: '/admin/partner',
      name: 'new-partner',
      component: () => import('@/views/cms/Partner'),
      meta: {
        requiresAuthentication: true,
        adminOnly: true,
        title: 'New Partner Page'
      }
    },
    {
      path: '/admin/partner/:id',
      name: 'edit-partner',
      props: true,
      component: () => import('@/views/cms/Partner'),
      meta: {
        requiresAuthentication: true,
        adminOnly: true,
        title: 'Edit Partner Page'
      }
    },
    {
      path: '/admin/marketing-pack/:id?',
      name: 'admin-marketing-pack',
      props: true,
      component: () => import('@/views/cms/MarketingPackPage'),
      meta: {
        requiresAuthentication: true,
        adminOnly: true,
        title: 'Marketing Pack Page'
      }
    },
    {
      path: '/admin/article',
      name: 'new-article',
      component: () => import('@/views/cms/News.vue'),
      meta: {
        requiresAuthentication: true,
        adminOnly: true
      }
    },
    {
      path: '/admin/news/:id',
      name: 'edit-article',
      props: true,
      component: () => import('@/views/cms/News.vue'),
      meta: {
        requiresAuthentication: true,
        adminOnly: true
      }
    },
    {
      path: '/notifications',
      name: 'notifications',
      component: Notifications,
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/admin/notify',
      name: 'notify',
      component: () => import('@/views/cms/Notification'),
      meta: {
        requiresAuthentication: true,
        adminOnly: true
      }
    },
    {
      path: '/admin/event',
      name: 'new-event',
      component: () => import('@/views/cms/Event'),
      meta: {
        requiresAuthentication: true,
        adminOnly: true
      }
    },
    {
      path: '/admin/event/:id',
      name: 'edit-event',
      component: () => import('@/views/cms/Event'),
      props: true,
      meta: {
        requiresAuthentication: true,
        adminOnly: true
      }
    },
    {
      path: '/profile',
      name: 'profile',
      component: Profile,
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/profile-v2',
      name: 'profileV2',
      component: ProfileV2,
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/profile-v2/:network?',
      name: 'profileV2Social',
      component: ProfileV2,
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/profile/:network?',
      name: 'ProfileSocial',
      component: Profile,
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/about',
      name: 'about',
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "about" */ './views/About'),
      meta: {
        requiresAuthentication: false
      }
    },
    {
      path: '/property-feeds',
      name: 'propertyfeeds',
      component: PropertyFeeds,
      meta: {
        requiresAuthentication: true,
        propertyCMSRoute: true
      }
    },
    {
      path: '/qr-generator',
      name: 'qrgenerator',
      component: QrGenerator,
      meta: {
        requiresAuthentication: true,
        propertyCMSRoute: true
      }
    },
    {
      path: '/property-management',
      name: 'propertymanagement',
      component: PropertyManagement,
      meta: {
        requiresAuthentication: true,
        propertyCMSRoute: true
      }
    },
    {
      path: '/property-management/:property',
      name: 'propertycontentmanagement',
      component: PropertyContentManagement,
      meta: {
        requiresAuthentication: true,
        propertyCMSRoute: true
      }
    },
    {
      path: '/admin/alert',
      name: 'new-alert',
      component: () => import('@/views/cms/Alert'),
      meta: {
        requiresAuthentication: true,
        adminOnly: true
      }
    },
    {
      path: '/admin/alert/:id',
      name: 'edit-alert',
      component: () => import('@/views/cms/Alert'),
      props: true,
      meta: {
        requiresAuthentication: true,
        adminOnly: true
      }
    },
    {
      path: '/news',
      name: 'news',
      component: News,
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/availability',
      name: 'AvailabilityCalendar',
      component: AvailabilityCalendar,
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/our-partners',
      name: 'Partners',
      component: Partners,
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/marketing-packs',
      name: 'MarketingPackIndex',
      component: () => import('@/views/MarketingPack'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/marketing-packs/payment-complete',
      name: 'MarketingPackOrderPaymentComplete',
      props: true,
      component: () => import('@/views/marketing-pack/MarketingPackOrderPaymentComplete'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/marketing-packs/:slug',
      name: 'MarketingPackPage',
      props: true,
      component: () => import('@/views/MarketingPackPage'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/fc-annual-review',
      name: 'fcAnnualReview',
      component: FcAnnualReview,
      props: true,
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/guild-annual-review',
      name: 'guildAnnualReview',
      component: GuildAnnualReview,
      props: true,
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/propqual-registration',
      name: 'PropQualRegistration',
      component: () => import('@/views/PropQualRegistration'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/propqual-registration/success',
      name: 'PropQualSuccess',
      component: () => import('@/views/propqual/PropQualSuccess'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/propqual-registration/cancel',
      name: 'PropQualCancelled',
      component: () => import('@/views/propqual/PropQualCancelled'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/print-order-form',
      name: 'PrintOrderForm',
      component: () => import('@/views/PrintOrderForm'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/print-order-form/basket',
      name: 'PrintOrderBasket',
      component: () => import('@/views/PrintOrderBasket'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/print-order-form/saved-items',
      name: 'PrintOrderSavedItems',
      component: () => import('@/views/PrintOrderSavedItems'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/print-order-form/saved-items/:itemId',
      name: 'PrintOrderFormItem',
      props: true,
      component: () => import('@/views/PrintOrderForm'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/print-order-form/order-history',
      name: 'PrintOrderHistory',
      component: () => import('@/views/PrintOrderHistory'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/print-order-form/order-details/:itemId',
      name: 'PrintOrderItemDetails',
      props: true,
      component: () => import('@/views/PrintOrderItemDetails'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/print-order-form/payment-complete',
      name: 'PrintOrderPaymentComplete',
      component: () => import('@/views/PrintOrderPaymentComplete'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/suppliers/:supplierId',
      name: 'Supplier',
      component: () => import('@/views/suppliers/GuildSupplier'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/event-registration',
      name: 'ConferenceRegistration',
      component: () => import('@/views/ConferenceRegistration'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/event-registration/success',
      name: 'ConferenceSuccess',
      component: () => import('@/views/conference/RegistrationSuccess'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/event-registration/cancel',
      name: 'ConferenceCancelled',
      component: () => import('@/views/conference/RegistrationCancelled'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/event-tickets/:eventTicketId/attendees/create',
      name: 'TicketEventBooking',
      component: () => import('@/views/events/TicketEventBooking'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/event-tickets/:orderId/success',
      name: 'TicketBookingSuccess',
      component: () => import('@/views/events/TicketBookingSuccess'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/event-tickets/:orderId/cancel',
      name: 'TicketBookingCancelled',
      component: () => import('@/views/events/TicketBookingCancelled'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/events-booked-by-me',
      name: 'EventBookedByList',
      component: () => import('@/views/events/EventBookedByList'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/events-booked-by-me/event-tickets/:conferenceId/list-attendees',
      name: 'EventBookedByAttendeesList',
      component: () => import('@/views/events/EventBookedByAttendeesList'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/events-booked-by-me/event-tickets/:conferenceId/attendee/:eventUserTicketId/edit',
      name: 'EventBookedByAttendeesEdit',
      component: () => import('@/views/events/EventBookedByAttendeesEdit'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/events-booked-for-me',
      name: 'EventBookedForList',
      component: () => import('@/views/events/EventBookedForList'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/events-booked-for-me/event-tickets/:conferenceId/attendee/:eventUserTicketId/edit',
      name: 'EventBookedForAttendeesEdit',
      component: () => import('@/views/events/EventBookedForAttendeesEdit'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/event-meetings-hosted-by',
      name: 'EventMeetingHostedList',
      component: () => import('@/views/events/EventMeetingHostedList'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/event-meetings-hosted-by/:meetingId/list-attendees',
      name: 'EventMeetingHostedAttendeesList',
      component: () => import('@/views/events/EventMeetingHostedAttendeesList'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/event-meetings-booked-for',
      name: 'EventMeetingBookedList',
      component: () => import('@/views/events/EventMeetingBookedList'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/associate-scheme-users',
      name: 'AssociateSchemeUsers',
      component: () => import('@/views/AssociateSchemeUsers'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/social/:network?',
      name: 'Social',
      component: Social,
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/easyproperty/auth',
      name: 'easy-property-sso',
      component: EasyPropertySSO,
      meta: {
        requiresAuthentication: false
      }
    },
    {
      path: '/17f3c41d-7dff-41d0-8ac6-c4e9032fe86a-42a0',
      name: 'GuildNewPlan',
      component: () => import('@/views/guild-plan/GuildNewPlan'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/guild-plan-standard',
      name: 'GuildPlan',
      component: () => import('@/views/guild-plan/GuildPlanStandard'),
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/:menuSlug/:slug',
      name: 'page',
      component: Page,
      props: true,
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/partner/:slug',
      name: 'partner-page',
      component: Page,
      props: true,
      meta: {
        requiresAuthentication: true
      }
    },
    {
      path: '/:menuSlug',
      name: 'menu',
      component: MenuPage,
      props: true,
      meta: {
        requiresAuthentication: true
      }
    },
    // This must remain the last route!
    {
      path: '*',
      name: '404',
      component: Error404
    }
  ]
})

router.beforeEach((to, from, next) => {
  const requiresAuthentication = to.meta.hasOwnProperty('requiresAuthentication')
    ? to.meta.requiresAuthentication
    : true

  const adminOnly = to.meta.hasOwnProperty('adminOnly')
    ? to.meta.adminOnly
    : false

  if (requiresAuthentication && !localStorage.getItem('accessToken')) {
    return
  }

  if (adminOnly && store.state.profile && store.state.profile.type > 2) {
    next(false)
    store.dispatch('displayToastNotification', {
      type: 'danger',
      icon: 'fa-lock',
      title: 'Unauthorized',
      info: 'You do not have permission to view that page'
    })
    return
  }

  if (store.state.system.title) {
    if (to.meta.title) {
      document.title = `${store.state.system.title} - ${to.meta.title}`
    } else {
      document.title = store.state.system.title
    }
  }

  // ignore propertyCMS routes if propertyCMS is disabled
  const requiresPropertyCMS = to.meta.hasOwnProperty('propertyCMSRoute')
    ? to.meta.propertyCMSRoute
    : false
  if (requiresPropertyCMS && (process.env.VUE_APP_DISABLEPROPERTYCMS === 'true')) {
    next('/')
    return
  }

  next()
})

export default router
